import { useAnnouncementStorage } from '../../../../ui/AppClients';
import { useAnnouncementsTranslation } from '../../../../ui/hooks';
import { HtmlOutput } from '@juvare/common-form-ui/HtmlOutput';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { ModalSlideout, ModalSlideoutContent, ModalSlideoutHeader, ModalSlideoutTitleArea, ModalSlideoutTopBar, ModalSlideoutTopBarDefaultActions, } from '@juvare/common-web-ui/ModalSlideout';
import { handleErrorSnackbar, showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { usePermission } from '@juvare/contact-management/ui/hooks';
import PlusIcon from '@mui/icons-material/Add';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import { Box, Button, Card, CardContent, CardHeader, Chip, FormControl, InputLabel, LinearProgress, List, ListItem, MenuItem, Select, Typography, } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';
import { allowManageAnnouncement } from '../utils/allowManageAnnouncements';
const LabelTypography = styled(Typography)(() => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    WebkitLineClamp: 4,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
}));
export const getAnnouncementsFilter = ({ orgMembership, hasSystemPermissionToManage, hasOrgPermissionToManage, announcementStatus, }) => {
    const sharedVisibilityFilter = {
        op: 'or',
        filter: [
            { column: 'public', op: '=', value: true },
            { column: 'visibility', op: 'overlap', value: orgMembership },
        ],
    };
    let filter = [
        {
            column: 'active',
            op: '=',
            value: true,
        },
        {
            op: 'or',
            filter: [
                { column: 'publishAt', op: 'is-null' },
                { column: 'publishAt', op: '<', value: Date.now() },
            ],
        },
        {
            op: 'or',
            filter: [
                { column: 'expiredAt', op: 'is-null' },
                { column: 'expiredAt', op: '>', value: Date.now() },
            ],
        },
        sharedVisibilityFilter,
    ];
    if (hasSystemPermissionToManage) {
        filter = [];
    }
    else if (hasOrgPermissionToManage) {
        filter = [sharedVisibilityFilter];
    }
    if (hasSystemPermissionToManage || hasOrgPermissionToManage) {
        if (announcementStatus !== 'all') {
            filter.push({
                column: 'active',
                op: '=',
                value: announcementStatus === 'active',
            });
        }
    }
    return filter;
};
export const AnnouncementsSlideout = ({ onEditAnnouncement, onCreate }) => {
    const { t, ready } = useAnnouncementsTranslation();
    const [open, setOpen] = React.useState(false);
    const [filterBy, setFilterBy] = React.useState('all');
    const [topAnnouncements, setTopAnnouncements] = React.useState([]);
    const [announcements, setAnnouncements] = React.useState([]);
    const [focusedAnnouncement, setFocusedAnnouncement] = React.useState();
    const announcementStorage = useAnnouncementStorage();
    const { requireAnySystemPermission, requireAnyOrgPermission, userState } = usePermission();
    const hasSystemPermissionToManage = requireAnySystemPermission('manage-announcements');
    const hasOrgPermissionToManage = requireAnyOrgPermission('manage-org-announcements');
    const hasSystemPermissionToDelete = requireAnySystemPermission('delete-announcements');
    const handleClose = () => {
        setOpen(false);
        setFocusedAnnouncement(undefined);
    };
    const getAnnouncements = React.useCallback((status, reloadTop) => {
        const filter = getAnnouncementsFilter({
            orgMembership: userState.orgMembership,
            hasSystemPermissionToManage,
            hasOrgPermissionToManage,
            announcementStatus: status,
        });
        announcementStorage
            .list({ filter, orderBy: [{ column: 'publishAt', desc: true }] })
            .then((response) => {
            setAnnouncements(response.rows);
            if (reloadTop) {
                const top3announcements = [];
                for (const announcement of response.rows) {
                    const publishAtValid = !announcement.columns.publishAt || announcement.columns.publishAt < Date.now();
                    const expiredAtValid = !announcement.columns.expiredAt || announcement.columns.expiredAt > Date.now();
                    if (top3announcements.length < 3 && publishAtValid && expiredAtValid) {
                        top3announcements.push(announcement);
                    }
                }
                setTopAnnouncements(top3announcements);
            }
        })
            .catch(handleErrorSnackbar);
    }, [announcementStorage, hasSystemPermissionToManage, hasOrgPermissionToManage, userState === null || userState === void 0 ? void 0 : userState.orgMembership]);
    React.useEffect(() => {
        getAnnouncements('all', true);
    }, [getAnnouncements]);
    const getMenuItems = (announcement) => {
        const menuItems = [];
        if (allowManageAnnouncement(announcement, userState)) {
            menuItems.push({
                label: t('common.edit'),
                onClick: () => onEditAnnouncement(announcement),
                'data-test': 'edit-menuitem',
            });
        }
        if (hasSystemPermissionToDelete) {
            menuItems.push({
                label: t('common.delete'),
                onClick: () => {
                    showConfirmDialog({
                        title: `${t('common.delete')} ${t('announcements.entity').toLowerCase()}`,
                        content: React.createElement(Typography, null, t('common.deleteQuestion', { item: t('announcements.entity').toLowerCase() })),
                        confirmLabel: t('common.delete'),
                        onConfirm: () => {
                            announcementStorage
                                .post({
                                action: 'delete',
                                id: announcement.id,
                            })
                                .then(() => {
                                var _a;
                                showInfoSnackbar({ message: t('common.itemDeleted', { item: (_a = announcement.columns) === null || _a === void 0 ? void 0 : _a.title }) });
                                getAnnouncements(filterBy, true);
                            })
                                .catch(handleErrorDialog);
                        },
                    });
                },
                'data-test': 'delete-menuitem',
            });
        }
        return menuItems;
    };
    const getTopAnnouncements = () => {
        return topAnnouncements.map((announcement) => (React.createElement(Box, { key: `top_${announcement.id}`, sx: {
                display: 'flex',
                alignItems: 'center',
            }, "data-test": 'top-announcement-row' },
            React.createElement(LabelTypography, { sx: { mr: 2 }, "data-test": 'title-label' }, announcement.columns.title),
            React.createElement(Button, { sx: { mr: 2 }, color: 'primary', onClick: () => {
                    setOpen(true);
                    setFocusedAnnouncement(announcement.id);
                }, "data-test": 'learn-more-button' }, t('announcements.learnMore')))));
    };
    if (!ready) {
        return React.createElement(LinearProgress, null);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { sx: { display: 'flex', gap: 2 } },
            React.createElement(Typography, { variant: 'h6' }, t('announcements.announcements')),
            onCreate && (hasSystemPermissionToManage || hasOrgPermissionToManage) && (React.createElement(Button, { variant: 'contained', startIcon: React.createElement(PlusIcon, null), onClick: onCreate, "data-test": 'new-button' }, t('announcements.createNew'))),
            React.createElement(Button, { "data-test": 'see-all-button', color: 'primary', onClick: () => {
                    setOpen(true);
                    setFocusedAnnouncement(undefined);
                } }, t('announcements.seeAll'))),
        getTopAnnouncements(),
        React.createElement(ModalSlideout, { open: open, onClose: handleClose, "data-test": 'announcement-slideout' },
            React.createElement(Box, { sx: {
                    overflow: 'auto',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: themeColors.surface.primary,
                } },
                React.createElement(ModalSlideoutTopBar, { isOpaque: true, "data-test": 'top-bar' },
                    React.createElement(ModalSlideoutTopBarDefaultActions, { onClose: handleClose })),
                React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', flex: 1, gap: 2 } },
                    React.createElement(ModalSlideoutHeader, { "data-test": 'header' },
                        React.createElement(ModalSlideoutTitleArea, { ariaDescribeAllChildren: false, titleTypographyVariant: 'h5', title: t('announcements.announcements'), sx: { gap: 1, alignItems: 'center', justifyContent: 'space-between' }, "data-test": 'title-area' }, hasSystemPermissionToManage || hasOrgPermissionToManage ? (React.createElement(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                            } },
                            React.createElement(Typography, { sx: { fontSize: 14, mr: 2 } }, t('announcements.showing')),
                            React.createElement(FormControl, { variant: 'outlined', size: 'small', "data-test": 'filter-menu' },
                                React.createElement(InputLabel, { id: 'filter-menu-label' }, t('announcements.type')),
                                React.createElement(Select, { variant: 'outlined', sx: { minWidth: 96, fontSize: 14 }, value: filterBy, onChange: (evt) => {
                                        setFilterBy(evt.target.value);
                                        getAnnouncements(evt.target.value);
                                    }, labelId: 'filter-menu-label', label: t('announcements.type'), inputProps: { 'aria-label': t('announcements.filterMenu') } },
                                    React.createElement(MenuItem, { sx: { fontSize: 14 }, value: 'all', "data-test": 'all-menuitem' }, "All"),
                                    React.createElement(MenuItem, { sx: { fontSize: 14 }, value: 'active', "data-test": 'active-menuitem' }, "Active"),
                                    React.createElement(MenuItem, { sx: { fontSize: 14 }, value: 'inactive', "data-test": 'inactive-menuitem' }, "Inactive"))))) : null)),
                    React.createElement(ModalSlideoutContent, { ariaDescribeAllChildren: false },
                        React.createElement(Box, { sx: { px: 2 } }, announcements.length ? (React.createElement(List, null, announcements.map((announcement) => {
                            var _a, _b, _c;
                            const focusedStyles = focusedAnnouncement && announcement.id === focusedAnnouncement
                                ? { backgroundColor: '#bce8ff' }
                                : {};
                            return (React.createElement(ListItem, { sx: { p: 0 }, key: announcement.id },
                                React.createElement(Card, { key: announcement.id, sx: Object.assign(Object.assign({ mb: 2 }, focusedStyles), { width: '100%' }), "data-test": 'announcement-row', "data-test-focused": announcement.id === focusedAnnouncement ? true : undefined },
                                    React.createElement(CardHeader, { action: allowManageAnnouncement(announcement, userState) ||
                                            hasSystemPermissionToDelete ? (React.createElement(React.Fragment, null,
                                            ((_a = announcement.columns) === null || _a === void 0 ? void 0 : _a.active) ? (React.createElement(Chip, { sx: {
                                                    color: themeColors.accent.green.bold,
                                                    borderColor: themeColors.accent.green.bold,
                                                }, variant: 'outlined', size: 'small', label: 'active', "data-test": 'active-chip' })) : null,
                                            React.createElement(MenuIconButton, { menuItems: getMenuItems(announcement), "data-test": 'menu-icon-button', "aria-label": t('common.more', {
                                                    title: announcement.columns.title,
                                                }) },
                                                React.createElement(MoreVertRounded, null)))) : null, title: React.createElement(LabelTypography, { "data-test": 'title-label', variant: 'subtitle1' }, announcement.columns.title), subheader: React.createElement(Typography, { sx: { fontSize: 12 }, color: 'textSecondary', "data-test": 'publish-at-label' }, `${((_b = announcement.columns) === null || _b === void 0 ? void 0 : _b.publishAt)
                                            ? new Date(announcement.columns.publishAt).toLocaleDateString()
                                            : ''}
                                              ${((_c = announcement.columns) === null || _c === void 0 ? void 0 : _c.expiredAt)
                                            ? ' - ' + new Date(announcement.columns.expiredAt).toLocaleDateString()
                                            : ''}
                                              `) }),
                                    React.createElement(CardContent, { sx: Object.assign({ backgroundColor: '#fafafa' }, focusedStyles), "data-test": 'body' },
                                        React.createElement(HtmlOutput, { html: announcement.columns.body, customTags: {
                                                p: {
                                                    element: (props) => (React.createElement(Typography, { sx: {
                                                            wordBreak: 'break-word',
                                                            whiteSpace: 'pre-wrap',
                                                        }, variant: 'body2' }, props.children)),
                                                    attributes: [],
                                                },
                                            } })))));
                        }))) : (React.createElement(Typography, null, t('announcements.noAnnouncements'))))))))));
};
